import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import InnerLayout from "../../../components/layouts/inner-layout";
import AddButton, { DraftButton } from "../../../components/buttons/button";
import SqlEditor from "../../../components/custom-reports/sql-editor";
import SchemaBrowser from "../../../components/custom-reports/schema-browser";
import CustomReportTable from "../../../components/custom-reports/custom-report-table";
import ExportDropdown from "../../../components/custom-reports/export-dropdown";
import SaveReportModal from "../../../components/custom-reports/save-report-modal";
import {
  customReportSchemaAction,
  customReportExecuteAction,
  customReportSaveAction,
  customReportUpdateAction,
  customReportGetAction,
} from "../../../redux/actions/custom-report-action";
import { PlayIcon } from "@heroicons/react/24/outline";
import { canCreateAndEditCustomReports } from "../../../utils/custom-report-permissions";

const CustomReportEditor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reportId } = useParams();

  // Check permission - redirect if not allowed
  useEffect(() => {
    if (!canCreateAndEditCustomReports()) {
      navigate("/reports", { replace: true });
    }
  }, [navigate]);

  const [sqlQuery, setSqlQuery] = useState("");
  const [schema, setSchema] = useState([]);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [results, setResults] = useState({ columns: [], rows: [], rowCount: 0, truncated: false });
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  useEffect(() => {
    fetchSchema();
    if (reportId) {
      fetchReport(reportId);
    }
  }, [reportId]);

  const fetchSchema = () => {
    setSchemaLoading(true);
    dispatch(
      customReportSchemaAction(
        (response) => {
          setSchema(response.data || []);
          setSchemaLoading(false);
        },
        (error) => {
          console.error("Failed to fetch schema:", error);
          setSchemaLoading(false);
        }
      )
    );
  };

  const fetchReport = (id) => {
    dispatch(
      customReportGetAction(
        id,
        (response) => {
          const report = response.data;
          setCurrentReport(report);
          setSqlQuery(report.sql_query);
        },
        (error) => {
          console.error("Failed to fetch report:", error);
        }
      )
    );
  };

  const handleRunQuery = () => {
    if (!sqlQuery.trim()) return;
    setLoading(true);
    setError(null);
    setResults({ columns: [], rows: [], rowCount: 0, truncated: false });

    dispatch(
      customReportExecuteAction(
        { sql_query: sqlQuery },
        (response) => {
          setResults(response.data);
          setLoading(false);
        },
        (errorResponse) => {
          setError(errorResponse?.data?.message || errorResponse?.message || "Query execution failed");
          setLoading(false);
        }
      )
    );
  };

  const handleColumnClick = (tableName, columnName) => {
    const textToInsert = `${tableName}.${columnName}`;
    setSqlQuery((prev) => prev + (prev ? " " : "") + textToInsert);
  };

  const handleSave = ({ name, description, is_shared }) => {
    setSaveLoading(true);
    const action = currentReport ? customReportUpdateAction : customReportSaveAction;
    const payload = currentReport
      ? { report_id: currentReport.report_id, name, description, sql_query: sqlQuery, is_shared }
      : { name, description, sql_query: sqlQuery, is_shared };

    dispatch(
      action(
        payload,
        (response) => {
          setSaveLoading(false);
          setSaveModalOpen(false);
          if (!currentReport && response.data?.report_id) {
            navigate(`/reports/custom-reports/editor/${response.data.report_id}`, { replace: true });
            setCurrentReport({ report_id: response.data.report_id, name, description, is_shared });
          }
          if (currentReport) {
            setCurrentReport({ ...currentReport, name, description, is_shared });
          }
        },
        (error) => {
          console.error("Failed to save report:", error);
          setError(error?.data?.message || error?.message || "Failed to save report");
          setSaveLoading(false);
        }
      )
    );
  };

  return (
    <InnerLayout
      title={currentReport?.name || "New Custom Report"}
      backBtn={true}
      list={true}
    >
      <div className="flex h-[calc(100vh-120px)]">
        {/* Schema Browser Sidebar */}
        <div className="w-64 flex-shrink-0 border-r border-EAECF0">
          {schemaLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <SchemaBrowser
              tables={schema}
              onColumnClick={handleColumnClick}
              searchValue={schemaSearch}
              setSearchValue={setSchemaSearch}
            />
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* SQL Editor Section */}
          <div className="p-4 border-b border-EAECF0">
            <SqlEditor
              value={sqlQuery}
              onChange={setSqlQuery}
              height="180px"
              schema={schema}
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <AddButton
                  text="Run Query"
                  icon={<PlayIcon className="w-4 h-4" />}
                  onclkFn={handleRunQuery}
                  loading={loading}
                  isDisabled={!sqlQuery.trim()}
                />
                <DraftButton
                  text="Save"
                  onclkFn={() => setSaveModalOpen(true)}
                  isDisabled={!sqlQuery.trim()}
                />
              </div>
              <ExportDropdown
                data={results.rows}
                columns={results.columns}
                reportName={currentReport?.name || "Custom Report"}
                disabled={results.rows.length === 0}
              />
            </div>
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-F94B4B rounded-md">
                <p className="text-xs text-F51D00">{error}</p>
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <CustomReportTable
                columns={results.columns}
                rows={results.rows}
                rowCount={results.rowCount}
                truncated={results.truncated}
              />
            )}
          </div>
        </div>
      </div>

      <SaveReportModal
        isOpen={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSave}
        initialName={currentReport?.name || ""}
        initialDescription={currentReport?.description || ""}
        initialIsShared={currentReport?.is_shared || false}
        isEdit={!!currentReport}
        loading={saveLoading}
      />
    </InnerLayout>
  );
};

export default CustomReportEditor;
